import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { useId } from '../../core/hooks/useId.js';
import { OverlayStackingProvider } from '../../core/providers/OverlayStackingProvider.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';

export const useKeepMounted = ({
  children,
  keepMounted = false,
}: {
  children: ReactNode;
  keepMounted: boolean;
}) => {
  const id = useId();

  if (!keepMounted) {
    return <div style={{ height: '100%' }}>{children}</div>;
  }

  return (
    <KeepMounted id={id} style={{ height: '100%' }}>
      {children}
    </KeepMounted>
  );
};

interface KeepMountedProps extends WithChildren, StylingProps {
  id: string;
}

/**
 * The `KeepMounted` component uses the `KeepMountedContext` to render the children into
 * a portal in order to avoid unmounting in-between renders.
 * Since the portal itself does not change and is only appended to the div rendered in the
 * `KeepMounted` component, even though the parent component may change, the children stay mounted.
 * The `KeepMounted` component has to be rendered all the time for this to work (e.g. when conditionally
 * rendering a component, if the component itself is not rendered, the `KeepMounted` component still
 * has to be rendered).
 */
const KeepMounted = (props: KeepMountedProps) => {
  const { children, style, className, id } = props;

  const { getPortalElement } = useContext(keepMountedContext);
  const keepMountedRef = useRef<HTMLDivElement | null>(null);

  const appendPortalElement = useCallback(() => {
    const portalElement = getPortalElement(id, children);
    if (portalElement === undefined) {
      return;
    }
    // Append the already existing portal with all it's contents to the
    // container rendered by the `KeepMounted` component.
    keepMountedRef.current?.appendChild(portalElement);
  }, [children, getPortalElement, id]);

  useEffect(() => {
    appendPortalElement();
  }, [appendPortalElement]);

  return <div style={style} className={className} ref={keepMountedRef} />;
};

const keepMountedContext = createContext<{
  getPortalElement: (
    id: string,
    children: ReactNode,
  ) => HTMLElement | undefined;
}>({
  getPortalElement: () => undefined,
});
keepMountedContext.displayName = 'KeepMountextContext';

/**
 * Provider keeping a map of the children to be rendered in a `KeepMounted` component and
 * the element the children should be portalled to.
 * @internal
 */
export const KeepMountedProvider = ({ children }: WithChildren) => {
  // Map of portals whose content should be kept mounted, even if the parent component unmounts.
  const [portals, setPortals] = useState(
    () => new Map<string, { children: ReactNode; element: HTMLElement }>(),
  );

  /** Get the element to portal the children to from the map of portals. */
  const getPortalElement = useCallback(
    (id: string, children: ReactNode) => {
      const keepPortal = portals.get(id);
      // If not yet available, create a portal container to portal the given
      // children to and store it in the set of portals.
      if (!keepPortal) {
        const element = document.createElement('div');
        setPortals(new Map(portals.set(id, { children, element })));
        return element;
      }

      // If we already have a portal, but the rendered children changed, update
      // the portals to render the updated children.
      if (keepPortal.children !== children) {
        setPortals(new Map(portals.set(id, { ...keepPortal, children })));
      }

      return keepPortal?.element;
    },
    [portals],
  );

  const ctx = useMemo(() => ({ getPortalElement }), [getPortalElement]);

  return (
    <keepMountedContext.Provider value={ctx}>
      {children}
      <OverlayStackingProvider>
        {Array.from(portals.entries()).map(([id, { children, element }]) => {
          return createPortal(children, element, id);
        })}
      </OverlayStackingProvider>
    </keepMountedContext.Provider>
  );
};
